import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, ProgressBar, Row, Spinner } from 'react-bootstrap';
import API from '../services/api';
import { updateStoredUser } from '../utils/sessionUser';
import './ContractorPages.css';

const MAX_INLINE_MB = 5;
const bytesOk = (dataUrl) => {
  const b64 = (String(dataUrl).split(',')[1] || '').trim();
  const bytes = Math.floor((b64.length * 3) / 4);
  return bytes <= MAX_INLINE_MB * 1024 * 1024;
};
const toDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onloadend = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
const isValidGST = (v = '') =>
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(String(v).trim());
const panFromGST = (gst = '') => String(gst || '').trim().toUpperCase().slice(2, 12);

const ContractorProfileForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [gstLookupLoading, setGstLookupLoading] = useState(false);
  const lastGstLookupRef = useRef('');
  const [form, setForm] = useState({
    contractorName: '',
    logo: '',
    millName: '',
    gstNumber: '',
    panNumber: '',
    address: '',
    contactPerson: '',
    contactMobile: '',
    notes: '',
  });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await API.get('/contractor/profile');
      const p = res.data || {};
      setForm({
        contractorName: p.contractorName || '',
        logo: p.logo || '',
        millName: p.millName || '',
        gstNumber: p.gstNumber || '',
        panNumber: p.panNumber || '',
        address: p.address || '',
        contactPerson: p.contactPerson || '',
        contactMobile: p.contactMobile || '',
        notes: p.notes || '',
      });
      updateStoredUser({
        fullName: p.contractorName || 'Contractor',
        photo: p.logo || '',
        photoUpdatedAt: Date.now(),
      });
      setIsUpdate(true);
    } catch (e) {
      if (e?.response?.status !== 404) {
        setError(e?.response?.data?.message || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'gstNumber') {
      const gst = String(value || '').toUpperCase();
      setForm((prev) => ({
        ...prev,
        gstNumber: gst,
        panNumber: gst.length >= 12 ? panFromGST(gst) : prev.panNumber,
      }));
      if (isValidGST(gst)) runGstLookup(gst);
      return;
    }
    if (name === 'panNumber') {
      setForm((prev) => ({ ...prev, panNumber: String(value || '').toUpperCase() }));
      return;
    }
    if (name === 'contactMobile') {
      setForm((prev) => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 15) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const completion = useMemo(() => {
    const has = (v) => String(v || '').trim().length > 0;
    const checks = [
      has(form.contractorName),
      has(form.logo),
      has(form.millName),
      has(form.gstNumber) && isValidGST(form.gstNumber),
      has(form.contactPerson),
      has(form.contactMobile),
      has(form.address),
      has(form.notes),
    ];
    const done = checks.filter(Boolean).length;
    const percent = Math.round((done / checks.length) * 100);
    return {
      percent,
      basic: [has(form.contractorName), has(form.logo), has(form.millName), has(form.gstNumber), has(form.address)].filter(Boolean).length,
      contact: [has(form.contactPerson), has(form.contactMobile)].filter(Boolean).length,
      notes: has(form.notes),
    };
  }, [form]);

  const applyGstProfileDetails = (data = {}) => {
    const companyName = data.companyName || data.tradeName || data.legalName;
    const address = [data.address, data.city, data.state, data.pincode].filter(Boolean).join(', ');
    setForm((prev) => ({
      ...prev,
      contractorName: data.contactName || prev.contractorName || companyName || '',
      millName: companyName || prev.millName,
      panNumber: data.panNumber || prev.panNumber,
      address: address || prev.address,
      contactPerson: data.contactName || prev.contactPerson,
      contactMobile: data.mobile || prev.contactMobile,
    }));
  };

  const runGstLookup = async (gstNumber) => {
    const gst = String(gstNumber || '').trim().toUpperCase();
    if (!isValidGST(gst) || gst === lastGstLookupRef.current) return;
    try {
      lastGstLookupRef.current = gst;
      setGstLookupLoading(true);
      setError('');
      const res = await API.get('/contractor/gst-lookup', { params: { gstNumber: gst } });
      applyGstProfileDetails(res.data || {});
      setMessage('GST details auto-filled.');
    } catch (err) {
      lastGstLookupRef.current = '';
      setError(err?.response?.data?.message || 'GST lookup failed.');
    } finally {
      setGstLookupLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    if (!/^\d{10,15}$/.test(form.contactMobile || '')) {
      setError('Contact mobile 10-15 digits ka hona chahiye');
      setSaving(false);
      return;
    }

    try {
      if (isUpdate) {
        await API.put('/contractor/profile', form);
        setMessage('Profile updated successfully');
      } else {
        await API.post('/contractor/profile', form);
        setMessage('Profile created successfully');
        setIsUpdate(true);
      }

      updateStoredUser({
        fullName: form.contractorName || 'Contractor',
        photo: form.logo || '',
        photoUpdatedAt: Date.now(),
      });
      await load();
    } catch (e1) {
      setError(e1?.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await toDataURL(file);
      if (!bytesOk(dataUrl)) {
        setError(`Image too large. Please upload ≤ ${MAX_INLINE_MB}MB.`);
        e.target.value = '';
        return;
      }
      setForm((prev) => ({ ...prev, logo: dataUrl }));
      setMessage('Logo selected. Save profile to apply.');
      setError('');
    } catch (_err) {
      setError('Logo read failed. Please try another image.');
    } finally {
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="contractor-profile-page">
      <Row className="g-3">
        <Col lg={4}>
          <Card className="contractor-card contractor-profile-preview sticky-lg-top">
            <Card.Body>
              <div className="contractor-avatar-wrap">
                {form.logo ? (
                  <img className="contractor-avatar-img" src={form.logo} alt="Contractor logo" />
                ) : (
                  <div className="contractor-avatar">{(form.contractorName || 'C').charAt(0).toUpperCase()}</div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleLogoPick}
              />
              <div className="text-center mb-2">
                <Button size="sm" variant="outline-secondary" onClick={() => fileRef.current?.click()}>
                  Change Image
                </Button>
              </div>
              <h4 className="mb-1 text-center">{form.contractorName || 'Contractor Name'}</h4>
              <div className="text-muted text-center mb-3">{form.millName || 'Mill name not added'}</div>

              <div className="contractor-score-wrap w-100 text-start mb-3">
                <div className="contractor-score-label">Profile Completion</div>
                <div className="contractor-score-value">{completion.percent}/100</div>
                <ProgressBar now={completion.percent} className="mt-2" />
              </div>

              <div className="contractor-preview-list">
                <div><span>Contact Person</span><strong>{form.contactPerson || '—'}</strong></div>
                <div><span>Mobile</span><strong>{form.contactMobile || '—'}</strong></div>
                <div><span>Address</span><strong>{form.address || '—'}</strong></div>
              </div>

                <div className="contractor-mini-kpis mt-3">
                <div>
                  <small>Basic</small>
                  <strong>{completion.basic}/5</strong>
                </div>
                <div>
                  <small>Contact</small>
                  <strong>{completion.contact}/2</strong>
                </div>
                <div>
                  <small>Notes</small>
                  <strong>{completion.notes ? 'Yes' : 'No'}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="contractor-card contractor-profile-form-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h4 className="mb-0">{isUpdate ? 'Update Contractor Profile' : 'Create Contractor Profile'}</h4>
                <div className="text-muted small">Required: Contractor Name, Contact Mobile</div>
              </div>

              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={onSubmit}>
                <div className="contractor-form-section">
                  <h6>Basic Information</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Contractor Name</Form.Label>
                        <Form.Control name="contractorName" value={form.contractorName} onChange={onChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Mill Name</Form.Label>
                        <Form.Control name="millName" value={form.millName} onChange={onChange} placeholder="e.g. Lotus Rice Mill Pvt. Ltd." />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>GST Number</Form.Label>
                        <Form.Control
                          name="gstNumber"
                          value={form.gstNumber}
                          onChange={onChange}
                          isInvalid={!!form.gstNumber && !isValidGST(form.gstNumber)}
                          placeholder="27ABCDE1234F1Z5"
                        />
                        <Form.Control.Feedback type="invalid">Enter a valid GST.</Form.Control.Feedback>
                        {gstLookupLoading && <Form.Text className="text-primary">Loading GST details...</Form.Text>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>PAN Number</Form.Label>
                        <Form.Control name="panNumber" value={form.panNumber} onChange={onChange} placeholder="ABCDE1234F" />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control as="textarea" rows={2} name="address" value={form.address} onChange={onChange} placeholder="Pickup / work location address" />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="contractor-form-section">
                  <h6>Contact Information</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Contact Person</Form.Label>
                        <Form.Control name="contactPerson" value={form.contactPerson} onChange={onChange} placeholder="e.g. Site Supervisor" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Contact Mobile</Form.Label>
                        <Form.Control name="contactMobile" value={form.contactMobile} onChange={onChange} required placeholder="10-15 digits" />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="contractor-form-section">
                  <h6>Work Notes</h6>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={4} name="notes" value={form.notes} onChange={onChange} placeholder="Daily workers, shifts, loading/unloading scope, special instructions" />
                  </Form.Group>
                </div>

                <div className="d-flex gap-2 mt-4">
                  <Button type="submit" disabled={saving}>{saving ? 'Saving...' : isUpdate ? 'Update Profile' : 'Create Profile'}</Button>
                  <Button type="button" variant="outline-secondary" onClick={load}>Refresh</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ContractorProfileForm;
